/**
 * CollectionDeleteModal — confirmation modal for deleting a collection.
 *
 * Design.md §13a.1 (verbatim body copy):
 *   `Delete "Italian"? Recipes inside stay in your library — only the
 *    collection is removed.`
 *
 * Design invariants (design.md §2):
 *   - Em dash (—) in body copy, not en dash or double hyphen.
 *   - Confirm button is `Delete` (not "OK" or "Got it").
 *   - Cancel button is `Cancel` (not "Go back" or "Never mind").
 *   - Destructive button uses `--del` accent variant (muted brick red).
 *     Per "one accent reserved" rule the confirm button uses the
 *     destructive colour token exclusively for this single destructive CTA.
 *   - No standard --accent on the destructive button.
 *   - Hairlines not shadows (--shadow-cta only on primary non-destructive CTAs).
 *   - No exclamations, no banned words (branding.md §5.2.1).
 *
 * Accessibility: focus trap, ESC closes, role=dialog, aria-modal.
 * Mobile: bottom-sheet at ≤ 640px; centred sheet on tablet/desktop.
 */

import { useState, useEffect, useRef } from "react";

// ─── Styles ───────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--scrim)",
  display: "flex",
  zIndex: 1000,
};

const SHEET_CSS = `
  @media (max-width: 639px) {
    .proof-col-delete-overlay {
      align-items: flex-end !important;
      justify-content: stretch !important;
    }
    .proof-col-delete-panel {
      border-radius: var(--radius-sheet-top) var(--radius-sheet-top) 0 0 !important;
      max-width: 100% !important;
      width: 100% !important;
    }
  }
  @media (min-width: 640px) {
    .proof-col-delete-overlay {
      align-items: center;
      justify-content: center;
    }
    .proof-col-delete-panel {
      width: 360px;
    }
  }
`;

const panelStyle: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: "var(--radius-card)",
  // --line-strong (not --line): readable panel edge on --bg; see RecipeCreationModal.
  border: "1px solid var(--line-strong)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "var(--space-4) var(--space-4) var(--space-3)",
  borderBottom: "1px solid var(--line)",
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--text)",
};

const closeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "var(--muted)",
  fontSize: "18px",
  lineHeight: 1,
  padding: "var(--space-1)",
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color var(--motion-state)",
};

const bodyStyle: React.CSSProperties = {
  padding: "var(--space-4)",
};

const bodyTextStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  color: "var(--text)",
  lineHeight: 1.5,
};

const errorStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--del)",
  marginTop: "var(--space-2)",
};

const footerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "var(--space-2)",
  justifyContent: "flex-end",
  padding: "var(--space-3) var(--space-4)",
  borderTop: "1px solid var(--line)",
  flexShrink: 0,
};

function cancelButtonStyle(): React.CSSProperties {
  return {
    background: "none",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-button)",
    padding: "var(--space-2) var(--space-4)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: "var(--muted)",
    cursor: "pointer",
    minHeight: "var(--tap-target-min)",
    transition: "color var(--motion-state), border-color var(--motion-state)",
  };
}

function deleteButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? "color-mix(in srgb, var(--del) 50%, transparent)" : "var(--del)",
    border: "none",
    borderRadius: "var(--radius-button)",
    padding: "var(--space-2) var(--space-4)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: "#f7f5f1",
    cursor: disabled ? "not-allowed" : "pointer",
    minHeight: "var(--tap-target-min)",
    transition: "opacity var(--motion-state), background var(--motion-state)",
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface CollectionDeleteModalProps {
  /** UUID of the collection to delete. */
  collectionUuid: string;
  /** Display name of the collection (used in confirmation body copy). */
  collectionName: string;
  /**
   * Called when the user confirms deletion.
   * Resolves on success; rejects with an Error on failure.
   */
  onDelete: (uuid: string) => Promise<void>;
  onClose: () => void;
}

export function CollectionDeleteModal({
  collectionUuid,
  collectionName,
  onDelete,
  onClose,
}: CollectionDeleteModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Focus the Cancel button on mount (safe default for destructive action).
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  // ESC closes the modal.
  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === overlayRef.current) onClose();
  }

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      await onDelete(collectionUuid);
      onClose();
    } catch {
      setError("Could not delete the collection. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <style>{SHEET_CSS}</style>
      <div
        ref={overlayRef}
        className="proof-col-delete-overlay"
        style={overlayStyle}
        role="dialog"
        aria-modal="true"
        aria-label={`Delete ${collectionName} collection`}
        onClick={handleOverlayClick}
      >
        <div className="proof-col-delete-panel" style={panelStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <span style={titleStyle}>Delete collection</span>
            <button type="button" style={closeButtonStyle} aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>

          {/* Body */}
          <div style={bodyStyle}>
            {/* Verbatim spec copy — design.md §13a.1 + Track 3.N constraint */}
            <p style={bodyTextStyle}>
              Delete &ldquo;{collectionName}&rdquo;? Recipes keep their other
              collections&nbsp;&mdash; only this collection is removed.
            </p>
            {error && (
              <p style={errorStyle} role="alert">
                {error}
              </p>
            )}
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            <button
              ref={cancelRef}
              type="button"
              style={cancelButtonStyle()}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              style={deleteButtonStyle(submitting)}
              disabled={submitting}
              onClick={() => void handleDelete()}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
